'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function RevenueCalculator() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [monthlyBookings, setMonthlyBookings] = useState(500);
  const [avgBookingValue, setAvgBookingValue] = useState(800);
  const [selectedTier, setSelectedTier] = useState('Professional');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const tiers = {
    'Starter': { share: 0.05, color: 'text-sky-blue' },
    'Professional': { share: 0.08, color: 'text-primary' },
    'Enterprise': { share: 0.12, color: 'text-success' }
  };

  const calculateRevenue = () => {
    const totalBookingValue = monthlyBookings * avgBookingValue;
    const revenueShare = totalBookingValue * tiers[selectedTier as keyof typeof tiers].share;
    const annualRevenue = revenueShare * 12;
    
    return {
      monthly: revenueShare,
      annual: annualRevenue,
      totalBookingValue
    };
  };

  const revenue = calculateRevenue();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card rounded-2xl shadow-brand-lg p-8 lg:p-12">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/3 mx-auto" />
              <div className="h-4 bg-muted rounded w-2/3 mx-auto" />
              <div className="h-64 bg-muted rounded mt-8" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="CalculatorIcon" size={20} className="text-primary" />
            <span className="text-sm font-medium text-primary">Revenue Projection Tool</span>
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Calculate Your Potential Revenue
          </h2>
          <p className="text-lg text-muted-foreground">
            See how much you could earn through our revenue-sharing partnership model. Adjust the parameters to match your business.
          </p>
        </div>

        {/* Calculator Interface */}
        <div className="bg-card rounded-2xl shadow-brand-lg overflow-hidden">
          <div className="grid lg:grid-cols-2">
            {/* Left Side - Inputs */}
            <div className="p-8 lg:p-12 space-y-8">
              <div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-6">
                  Your Business Parameters
                </h3>

                {/* Monthly Bookings Slider */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-foreground">
                      Monthly Bookings
                    </label>
                    <span className="text-lg font-heading font-bold text-primary">
                      {monthlyBookings.toLocaleString()}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="100"
                    max="5000"
                    step="100"
                    value={monthlyBookings}
                    onChange={(e) => setMonthlyBookings(Number(e.target.value))}
                    className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>100</span>
                    <span>5,000</span>
                  </div>
                </div>

                {/* Average Booking Value Slider */}
                <div className="space-y-4 mt-8">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-foreground">
                      Average Booking Value
                    </label>
                    <span className="text-lg font-heading font-bold text-primary">
                      {formatCurrency(avgBookingValue)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="200"
                    max="5000"
                    step="100"
                    value={avgBookingValue}
                    onChange={(e) => setAvgBookingValue(Number(e.target.value))}
                    className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>$200</span>
                    <span>$5,000</span>
                  </div>
                </div>

                {/* Partnership Tier Selection */}
                <div className="space-y-4 mt-8">
                  <label className="text-sm font-medium text-foreground block">
                    Partnership Tier
                  </label>
                  <div className="space-y-2">
                    {Object.entries(tiers).map(([tier, data]) => (
                      <button
                        key={tier}
                        onClick={() => setSelectedTier(tier)}
                        className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all duration-200 ${
                          selectedTier === tier
                            ? 'border-primary bg-primary/5' :'border-border hover:border-muted-foreground/30'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-4 h-4 rounded-full border-2 ${
                            selectedTier === tier
                              ? 'border-primary bg-primary' :'border-muted-foreground'
                          }`}>
                            {selectedTier === tier && (
                              <div className="w-full h-full rounded-full bg-white scale-50" />
                            )}
                          </div>
                          <span className="font-heading font-semibold text-foreground">
                            {tier}
                          </span>
                        </div>
                        <span className={`font-heading font-bold ${data.color}`}>
                          {(data.share * 100)}% Share
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Results */}
            <div className="bg-gradient-to-br from-primary to-secondary p-8 lg:p-12 text-white">
              <h3 className="font-heading text-xl font-semibold mb-8">
                Your Revenue Projection
              </h3>

              {/* Revenue Cards */}
              <div className="space-y-6">
                {/* Monthly Revenue */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="CurrencyDollarIcon" size={20} className="text-accent" />
                    <span className="text-sm font-medium text-white/80">Monthly Revenue</span>
                  </div>
                  <div className="font-heading text-4xl font-bold text-white">
                    {formatCurrency(revenue.monthly)}
                  </div>
                  <div className="text-sm text-white/60 mt-2">
                    From {monthlyBookings.toLocaleString()} bookings
                  </div>
                </div>

                {/* Annual Revenue */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="ChartBarIcon" size={20} className="text-accent" />
                    <span className="text-sm font-medium text-white/80">Annual Revenue</span>
                  </div>
                  <div className="font-heading text-4xl font-bold text-white">
                    {formatCurrency(revenue.annual)}
                  </div>
                  <div className="text-sm text-white/60 mt-2">
                    Projected yearly earnings
                  </div>
                </div>

                {/* Total Booking Value */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="BanknotesIcon" size={20} className="text-accent" />
                    <span className="text-sm font-medium text-white/80">Total Booking Value</span>
                  </div>
                  <div className="font-heading text-4xl font-bold text-white">
                    {formatCurrency(revenue.totalBookingValue)}
                  </div>
                  <div className="text-sm text-white/60 mt-2">
                    Monthly transaction volume
                  </div>
                </div>
              </div>

              {/* Additional Benefits */}
              <div className="mt-8 pt-8 border-t border-white/20">
                <h4 className="font-heading font-semibold mb-4">Additional Benefits</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircleIcon" size={18} className="text-accent mt-0.5" />
                    <span className="text-sm text-white/90">No setup or integration fees</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircleIcon" size={18} className="text-accent mt-0.5" />
                    <span className="text-sm text-white/90">24/7 technical support included</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircleIcon" size={18} className="text-accent mt-0.5" />
                    <span className="text-sm text-white/90">Marketing co-op opportunities</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircleIcon" size={18} className="text-accent mt-0.5" />
                    <span className="text-sm text-white/90">Access to analytics dashboard</span>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <button className="w-full mt-8 bg-coral hover:bg-[#FF5722] text-white py-4 rounded-lg font-heading font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300">
                Get Started with This Plan
              </button>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            * Revenue projections are estimates based on provided parameters. Actual earnings may vary based on booking patterns, seasonal fluctuations, and market conditions.
          </p>
        </div>
      </div>
    </section>
  );
}